import React, { useState, useEffect, useContext } from "react";
import { 
  AlertTriangle, Calendar, Send, RefreshCw, Clipboard, CheckCircle, 
  XCircle, Clock, FileText 
} from "lucide-react";
import { employeeWorkforceAPI } from "../../api/apiService";
import { NotificationContext } from "../../context/NotificationContext";
import EmptyState from "../../components/EmptyState";

export default function LeaveRequestsPage() {
  const { addNotification } = useContext(NotificationContext);
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);

  // Form State
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [leaveType, setLeaveType] = useState("");
  const [reason, setReason] = useState("");

  const fetchLeaves = async () => {
    setLoading(true);
    try {
      const res = await employeeWorkforceAPI.getLeaveRequests();
      setLeaves(res.data);
    } catch (err) {
      console.error(err);
      addNotification("Failed to fetch leave requests", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeaves();
  }, []);

  const handleApplyLeave = async (e) => {
    e.preventDefault();
    if (!startDate || !endDate) {
      addNotification("Please select both start and end dates.", "warning");
      return;
    }
    if (new Date(startDate) > new Date(endDate)) {
      addNotification("Start date cannot be after end date.", "warning");
      return;
    }
    if (!reason.trim()) {
      addNotification("Please provide a reason for the leave.", "warning");
      return;
    }

    setSubmitLoading(true);
    try {
      const payload = {
        start_date: startDate,
        end_date: endDate,
        leave_type: leaveType,
        reason: reason
      };
      await employeeWorkforceAPI.createLeaveRequest(payload);
      addNotification("Leave application submitted!", "success");
      
      // Reset Form
      setStartDate("");
      setEndDate("");
      setLeaveType("");
      setReason("");

      // Refresh list
      const res = await employeeWorkforceAPI.getLeaveRequests();
      setLeaves(res.data);
    } catch (err) {
      console.error(err);
      addNotification("Failed to submit leave application.", "error");
    } finally {
      setSubmitLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-slate-900">
        <EmptyState
          preset="leave"
          title="Loading Leave Applications..."
          message="Please wait a moment."
        />
      </div>
    );
  }

  const statusBadges = {
    "PENDING": "bg-amber-50 text-amber-600 dark:bg-amber-950/30 dark:text-amber-400 border border-amber-200 dark:border-amber-800",
    "APPROVED": "bg-green-50 text-green-600 dark:bg-green-950/30 dark:text-green-400 border border-green-200 dark:border-green-800",
    "REJECTED": "bg-red-50 text-red-600 dark:bg-red-950/30 dark:text-red-400 border border-red-200 dark:border-red-800",
  };

  const leaveTypesMap = {
    "CASUAL": "Casual Leave",
    "SICK": "Sick Leave",
    "EARNED": "Earned Leave",
    "UNPAID": "Unpaid Leave",
    "MATERNITY": "Maternity Leave",
    "PATERNITY": "Paternity Leave",
    "BEREAVEMENT": "Bereavement Leave"
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900 p-4 md:p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        
        {/* Header */}
        <div className="bg-white dark:bg-slate-800 p-6 md:p-8 rounded-3xl border border-gray-100 dark:border-slate-700 shadow-sm">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-green-600 rounded-2xl text-white shadow-lg shadow-green-500/20">
              <AlertTriangle className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-black text-gray-900 dark:text-white">Leave Management</h1>
              <p className="text-gray-700 dark:text-slate-300 font-medium">Apply for time-off, vacations, or sick leave, and track administrative approval status</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          
          {/* Apply Leave Form */}
          <div className="bg-white dark:bg-slate-800 p-6 rounded-3xl border border-gray-100 dark:border-slate-700 shadow-sm space-y-5">
            <h2 className="text-lg font-black text-gray-900 dark:text-white uppercase tracking-wider mb-2">
              Apply For Leave
            </h2>

            <form onSubmit={handleApplyLeave} className="space-y-4">
              
              {/* Leave Type */}
              <div>
                <label className="block text-xs font-bold text-gray-600 dark:text-slate-400 uppercase tracking-widest mb-2">
                  Leave Type
                </label>
                <select
                  className="w-full px-4 py-3 bg-gray-50 dark:bg-slate-900 border border-gray-100 dark:border-slate-700 rounded-2xl focus:ring-2 focus:ring-green-500 transition dark:text-white appearance-none"
                  value={leaveType}
                  onChange={(e) => setLeaveType(e.target.value)}
                  required
                >
                  <option value="" disabled>Select a leave type...</option>
                  <option value="CASUAL">Casual Leave</option>
                  <option value="SICK">Sick Leave</option>
                  <option value="EARNED">Earned Leave</option>
                  <option value="UNPAID">Unpaid Leave</option>
                  <option value="MATERNITY">Maternity Leave</option>
                  <option value="PATERNITY">Paternity Leave</option>
                  <option value="BEREAVEMENT">Bereavement Leave</option>
                </select>
              </div>

              {/* Start & End Dates */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-gray-600 dark:text-slate-400 uppercase tracking-widest mb-2">
                    Start Date
                  </label>
                  <div className="relative">
                    <Calendar className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="date"
                      className="w-full pl-10 pr-3 py-3 bg-gray-50 dark:bg-slate-900 border border-gray-100 dark:border-slate-700 rounded-2xl focus:ring-2 focus:ring-green-500 transition text-sm dark:text-white"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-600 dark:text-slate-400 uppercase tracking-widest mb-2">
                    End Date
                  </label>
                  <div className="relative">
                    <Calendar className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="date"
                      className="w-full pl-10 pr-3 py-3 bg-gray-50 dark:bg-slate-900 border border-gray-100 dark:border-slate-700 rounded-2xl focus:ring-2 focus:ring-green-500 transition text-sm dark:text-white"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Reason */}
              <div>
                <label className="block text-xs font-bold text-gray-600 dark:text-slate-400 uppercase tracking-widest mb-2">
                  Reason for Leave
                </label>
                <div className="relative">
                  <FileText className="absolute left-4 top-3.5 w-5 h-5 text-gray-400" />
                  <textarea
                    placeholder="Provide a detailed reason..."
                    rows="4"
                    className="w-full pl-12 pr-4 py-3 bg-gray-50 dark:bg-slate-900 border border-gray-100 dark:border-slate-700 rounded-2xl focus:ring-2 focus:ring-green-500 transition text-sm dark:text-white"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    required
                  />
                </div>
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={submitLoading}
                className="w-full py-3.5 bg-green-600 hover:bg-green-700 text-white rounded-2xl font-bold flex items-center justify-center gap-2 transition active:scale-95 shadow-md shadow-green-500/10"
              >
                {submitLoading ? (
                  <RefreshCw className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Submit Application
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Leave History List */}
          <div className="lg:col-span-2 bg-white dark:bg-slate-800 rounded-3xl border border-gray-100 dark:border-slate-700 shadow-sm overflow-hidden flex flex-col justify-between">
            <div>
              <div className="p-6 border-b border-gray-50 dark:border-slate-700">
                <h2 className="text-lg font-black text-gray-900 dark:text-white uppercase tracking-wider">
                  Leave Request History
                </h2>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead className="bg-gray-50 dark:bg-slate-900/50 border-b border-gray-100 dark:border-slate-700">
                    <tr>
                      <th className="px-6 py-4 text-xs font-black uppercase text-gray-600 dark:text-slate-400 tracking-wider">Type</th>
                      <th className="px-6 py-4 text-xs font-black uppercase text-gray-600 dark:text-slate-400 tracking-wider">Duration</th>
                      <th className="px-6 py-4 text-xs font-black uppercase text-gray-600 dark:text-slate-400 tracking-wider">Reason</th>
                      <th className="px-6 py-4 text-xs font-black uppercase text-gray-600 dark:text-slate-400 tracking-wider">Status</th>
                      <th className="px-6 py-4 text-xs font-black uppercase text-gray-600 dark:text-slate-400 tracking-wider">Admin Note</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-slate-700 text-sm">
                    {leaves.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="px-6 py-12 text-center text-gray-500">
                          No leave applications submitted yet.
                        </td>
                      </tr>
                    ) : leaves.map(item => {
                      const start = new Date(item.start_date);
                      const end = new Date(item.end_date);
                      const days = Math.round((end - start) / (1000 * 60 * 60 * 24)) + 1;

                      return (
                        <tr key={item.id} className="hover:bg-gray-50/50 dark:hover:bg-slate-800/30 transition-colors">
                          <td className="px-6 py-4 font-bold text-gray-900 dark:text-white">
                            {leaveTypesMap[item.leave_type] || item.leave_type}
                          </td>
                          <td className="px-6 py-4">
                            <p className="font-bold text-gray-700 dark:text-slate-300">
                              {start.toLocaleDateString([], { month: "short", day: "numeric" })} - {end.toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" })}
                            </p>
                            <p className="text-[10px] text-gray-600 dark:text-slate-500 font-bold uppercase tracking-wider mt-0.5">
                              {days} Day{days > 1 ? "s" : ""}
                            </p>
                          </td>
                          <td className="px-6 py-4 text-gray-500 dark:text-slate-400 max-w-[200px] truncate" title={item.reason}>
                            {item.reason}
                          </td>
                          <td className="px-6 py-4">
                            <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest ${statusBadges[item.status]}`}>
                              {item.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-xs text-gray-600 dark:text-slate-400 font-medium">
                            {item.rejection_reason || <span className="italic text-gray-300 dark:text-slate-600">None</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

        </div>

      </div>
    </div>
  );
}
